import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Nav from "../components/Nav";
import Loading from "../components/LoadingScreen";
import ConfigTabulka from "../components/ConfiguratorComp/ConfigTabulka";
import SiedmaTabulkaConfi from "../components/ConfiguratorComp/SiedmaTabConfi";
import Vybavenie from "../components/ConfiguratorComp/Vybavenie";

import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { FaSearch } from "react-icons/fa";

import sipka_find from "../images/icons8-arrow1.svg";
import ulozitImage from "../images/download-minimalistic-svgrepo-com.svg";

/* ------------------------------------------------------------------ */
/* Konštanty                                                           */
/* ------------------------------------------------------------------ */

const POCET_POZICII = 8;
const INIT_STLACENE = ["x", "-", "x", "x", "x", "x", "x", "/"];
const CLASSY_TABULKY = ["prva", "druha", "tretia", "stvrta", "piata", "siesta"];

// Pozícia 1 chýba zámerne – je to oddeľovač "-", nie tabuľka.
const INDEXY_TABULIEK = [0, 2, 3, 4, 5, 6, 7];

const BASE_URL_OBRAZKY = "https://konfigurator.regada.sk/obrazkyVsetky";

const prazdnePole = () => Array(POCET_POZICII).fill("");
const prazdneNajdene = () =>
  Object.fromEntries(INDEXY_TABULIEK.map((i) => [i, []]));
const prazdnePoznamky = () => Array.from({ length: 9 }, () => []);

const TEXTY = {
  objednavaciKod: {
    SK: "Objednávací kód",
    EN: "Order code",
    RU: "Марка исполнения",
  },
  standardneVybavenie: {
    SK: "Štandardné vybavenie:",
    EN: "Standard equipment:",
    RU: "Стандартное оснащение:",
  },
  rozsireneVybavenie: {
    SK: "Rozšírené vybavenie:",
    EN: "Additional equipment",
    RU: "Добавочное оснащение",
  },
  nezvolene: {
    SK: "Nezvolili ste si možnosť z následujúcich tabuliek:",
    EN: "You didn't choose from the following tables:",
    RU: "Вы не выбрали из следующих таблиц:",
  },
  zvoleneNacrty: {
    SK: "Zvolené náčrty",
    EN: "Selected drawings",
    RU: "Избранные эскизы",
  },
  zvoleneSchemy: {
    SK: "Zvolené schémy",
    EN: "Selected diagrams",
    RU: "Избранные схемы",
  },
  ziadne: { SK: "Žiadne", EN: "None", RU: "никто" },
  ukazat: { SK: "Ukázať", EN: "View", RU: "Вид" },
  zobrazitVsetko: {
    SK: "Zobraziť všetko",
    EN: "Show all",
    RU: "Показать все",
  },
  pokracovat: {
    SK: "Pokračovať na náhľad",
    EN: "Continue to preview",
    RU: "Продолжить просмотр",
  },
  zakazana: {
    SK: "Zakázaná kombinácia",
    EN: "Forbidden combination",
    RU: "Запрещенная комбинация",
  },
  chybaLomitko: {
    SK: "Lomítko chýba v objednávacom kóde.",
    EN: "The order code is missing a slash.",
    RU: "В коде заказа отсутствует косая черта.",
  },
};

/* ------------------------------------------------------------------ */
/* Čisté pomocné funkcie (bez stavu, preto mimo komponentu)             */
/* ------------------------------------------------------------------ */

/** Nahradí jednu položku v poli a vráti novú kópiu – nikdy nemutuje vstup. */
const nahradNaIndexe = (pole, index, hodnota) =>
  pole.map((p, i) => (i === index ? hodnota : p));

/**
 * Normalizovaný kľúč kombinácie: "CE" aj "EC" dajú rovnaký výsledok.
 * Pole zoradí po prvkoch, string po znakoch.
 */
const normKluc = (hodnota) => {
  const casti = Array.isArray(hodnota) ? hodnota : [...String(hodnota ?? "")];
  return casti
    .map((c) => String(c).trim())
    .sort()
    .join("");
};

/** Doplní kód na POCET_POZICII znakov, chýbajúce nahradí "/". */
const doPolaZnakov = (str) =>
  Array.from({ length: POCET_POZICII }, (_, i) => str[i] ?? "/");

/** Rozparsuje "GS 25.1234/AB" na typové číslo, pozície a koncovku. */
const rozparsujObjednavaciKod = (vstup) => {
  const lomitko = vstup.indexOf("/");
  const prvaCast = vstup
    .slice(0, lomitko)
    .replace(/\s/g, "")
    .toUpperCase();
  const druhaCast = vstup
    .slice(lomitko + 1)
    .replace(/\s/g, "")
    .toUpperCase();
  const bodka = prvaCast.indexOf(".");

  return {
    typoveHladane: prvaCast.slice(0, bodka),
    pozicie: doPolaZnakov(prvaCast.slice(bodka + 1)),
    koncovka: druhaCast,
  };
};

/** Viac schém v jednej bunke -> pole, jedna schéma -> string, "-" -> "". */
const schemyToArray = (schema) => {
  if (schema === "-") return "";
  const casti = String(schema)
    .replace(/,/g, "")
    .split(" ")
    .filter((e) => e !== "/");
  return casti.length === 1 ? schema : casti;
};

/**
 * Zo všetkých zvolených schém vyrobí výsledný string.
 * Ak je v jednej pozícii viac možností, vyberie tú, ktorá sa opakuje
 * aj inde (= spoločná pre viac tabuliek), duplikáty odstráni.
 */
const schemyAlgorithm = (rawSchemy) => {
  const arr = rawSchemy ?? [];

  // len položky, ktoré sú polia (viac možností)
  const flat = arr.flatMap((e) => (Array.isArray(e) ? e : []));

  // ktoré sa vyskytujú viackrát
  const dupes = new Set(flat.filter((item, i) => flat.indexOf(item) !== i));

  // pole nahradíme nájdenou spoločnou schémou
  const vybrane = arr.map((e) =>
    Array.isArray(e) ? e.find((x) => dupes.has(x)) ?? "" : e
  );

  // každú schému vypíšeme len raz
  const videne = new Set();
  return vybrane
    .filter((e) => {
      if (!e || videne.has(e)) return false;
      videne.add(e);
      return true;
    })
    .join(", ");
};

/**
 * Prejde názvy tabuľky spolu s riadkom a vytiahne schému a náčrt.
 * Vracia null, ak daná tabuľka taký stĺpec nemá – volajúci potom
 * ponechá predchádzajúcu hodnotu.
 */
const vytiahniSchemuANacrt = (riadok, nazvy) => {
  let schema = null;
  let nacrt = null;
  let pozicia = 0;

  for (const jedenNazov of nazvy) {
    // vnorený názov: [["Schéma zapojenia"], ["230 V AC", "24 V DC", ...]]
    if (Array.isArray(jedenNazov)) {
      const dlzka = jedenNazov[jedenNazov.length - 1].length;
      const titul = String(jedenNazov[0]?.[0] ?? "").split(" ")[0];
      const vyrez = riadok.slice(pozicia, pozicia + dlzka).join(", ");

      if (titul === "Schéma") schema = schemyToArray(vyrez);
      else if (titul === "Rozmerový") nacrt = vyrez;

      pozicia += dlzka;
      continue;
    }

    // bežný názov
    const titul = String(jedenNazov).split(" ")[0];
    const hodnota = riadok[pozicia];

    if (titul === "Schéma") {
      if (hodnota !== "nasl. tabuľka") schema = schemyToArray(hodnota);
    } else if (titul === "Rozmerový") {
      nacrt = hodnota;
    }

    pozicia += 1;
  }

  return { schema, nacrt };
};

/** Prvý (najvrchnejší) názov tabuľky, aj keď je vnorený. */
const nazovTabulky = (tabulka) => {
  const prvy = tabulka?.[0]?.nazvy?.[0];
  return Array.isArray(prvy) ? [prvy].flat(Infinity)[0] : prvy;
};

/** Preklad podľa slovníka; ak kľúč nepozná, vráti originál. */
const prelozSlovnik = (slovnik, kluc, jazyk) => {
  const zaznam = slovnik?.[kluc];
  if (!zaznam) return kluc;
  if (jazyk === "EN") return zaznam[0];
  if (jazyk === "RU") return zaznam[1];
  return kluc;
};

/** Poznámky v riadku sú tvaru "3)", "12)". */
const poznamkyZRiadka = (riadok) => {
  if (!riadok.join("").includes(")")) return null;
  return riadok
    .join(" ")
    .split(" ")
    .filter((cast) => cast.includes(")") && !isNaN(cast.slice(0, -1)));
};

/* ------------------------------------------------------------------ */
/* Objednávacie čísla rozšíreného vybavenia                            */
/* ------------------------------------------------------------------ */

const RozsireneCisla = ({ letters, kod, pocetCislic }) => {
  const vypln = (znak) =>
    Array.from({ length: pocetCislic }, (_, i) => (
      <div className="objednavaci_riadok cislo" key={`${znak}-${i}`}>
        {znak}
      </div>
    ));

  if (!letters) return null;
  if (letters.length === 0) return vypln("x");
  if (kod === undefined) return vypln("-");

  return kod.split("").map((znak, i) => (
    <div className="objednavaci_riadok cislo" key={`${i}-${znak}`}>
      {znak}
    </div>
  ));
};

/* ------------------------------------------------------------------ */
/* Konfigurátor                                                        */
/* ------------------------------------------------------------------ */

const Configurator = () => {
  const serverAdresa = process.env.REACT_APP_API_URL;
  const { typove } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* --- stav --------------------------------------------------------- */

  const [vsetkyUdaje, setVsetkyUdaje] = useState(null);
  const [slovnik, setSlovnik] = useState(null);
  const [nacitava, setNacitava] = useState(false);
  const [error, setError] = useState(null);

  const [stlacene, setStlacene] = useState(INIT_STLACENE);
  const [pismenka, setPismenka] = useState([]);
  const [zakaznicke, setZakaznicke] = useState(prazdnePole);
  const [celyRiadok, setCelyRiadok] = useState(() =>
    Array.from({ length: 7 }, () => [])
  );
  const [ktoryNazov, setKtoryNazov] = useState(prazdnePole);
  const [nacrtky, setNacrtky] = useState(prazdnePole);
  const [schemy, setSchemy] = useState(prazdnePole);

  const [najdene, setNajdene] = useState(prazdneNajdene);
  const [najdenePopis, setNajdenePopis] = useState(prazdneNajdene);
  const [najdenePoznamky, setNajdenePoznamky] = useState(prazdnePoznamky);

  const [ktoraTab, setKtoraTab] = useState(null);
  const [konektor, setKonektor] = useState(false);
  const [nevybrateMoznosti, setNevybrateMoznosti] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("SK");

  const [obchodak, setObchodak] = useState(false);
  const [zakladnaZlava, setZakladnaZlava] = useState(0);
  const [rozsirenaZlava, setRozsirenaZlava] = useState("");

  const [searchZapnuty, setSearchZapnuty] = useState(false);
  const [inputSearch, setInputSearch] = useState("");

  const [openNacrtImg, setOpenNacrtImg] = useState(false);
  const [openSchemyImg, setOpenSchemyImg] = useState(false);
  const [indexSchema, setIndexSchema] = useState(0);

  const t = useCallback(
    (kluc) => TEXTY[kluc]?.[selectedLanguage] ?? TEXTY[kluc]?.SK ?? "",
    [selectedLanguage]
  );

  /* --- odvodené dáta z vsetkyUdaje ---------------------------------- */

  const zakazanaMoznost = useMemo(
    () => vsetkyUdaje?.zakazana_moznost ?? [],
    [vsetkyUdaje]
  );

  const poznamka = useMemo(() => vsetkyUdaje?.poznamka ?? {}, [vsetkyUdaje]);

  /** Kombinácie písmen rozšíreného vybavenia -> objednávacie číslo. */
  const kombinacieSpolu = useMemo(() => {
    const tab7 = vsetkyUdaje?.tabulka7;
    if (!tab7) return {};

    const zHodnot = {};
    for (const riadok of tab7.hodnoty) {
      const posledna = riadok[riadok.length - 1];
      // ak je posledný stĺpec cena, čísla sú o jednu pozíciu vľavo
      const maCenu = typeof posledna === "number" && !isNaN(posledna);
      const offset = maCenu ? 3 : 2;
      zHodnot[riadok[0]] =
        riadok[riadok.length - offset] + riadok[riadok.length - offset + 1];
    }

    return { ...zHodnot, ...tab7.kombinacie };
  }, [vsetkyUdaje]);

  const kombinaciePismena = useMemo(
    () => Object.keys(kombinacieSpolu),
    [kombinacieSpolu]
  );

  /** Tie isté kombinácie, ale pod normalizovaným kľúčom. */
  const kombinacieNorm = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(kombinacieSpolu).map(([k, v]) => [normKluc(k), v])
      ),
    [kombinacieSpolu]
  );

  const kodPre = useCallback(
    (letters) => kombinacieNorm[normKluc(letters)],
    [kombinacieNorm]
  );

  /* --- odvodené zo stavu (žiadny duplikovaný state) ------------------ */

  const kodRozsireneho = kodPre(pismenka);

  // predtým to bol useState – teraz sa nemôže rozjesť s pismenka
  const dlhsieRozsireneCislo =
    kodRozsireneho && kodRozsireneho.length > 2 ? kodRozsireneho.length : false;
  const pocetCislic = dlhsieRozsireneCislo || 2;

  const kombinaciaPovolena =
    kodRozsireneho !== undefined
      ? !najdene[7].includes(kodRozsireneho)
      : pismenka.length === 0;

  const schemyText = useMemo(() => schemyAlgorithm(schemy), [schemy]);
  const schemyList = useMemo(
    () => (schemyText ? schemyText.split(", ") : []),
    [schemyText]
  );
  const maSchemy = schemyList.length > 0;
  const maNacrty = useMemo(() => nacrtky.some((e) => e !== ""), [nacrtky]);

  const obrazkySchem = useMemo(
    () => schemyList.map((v) => `${BASE_URL_OBRAZKY}/schemy/${v}.bmp`),
    [schemyList]
  );

  const chybajuceTabulky = stlacene.includes("x") || stlacene.includes("X");

  /** Všetko, čo náhľad potrebuje – na jednom mieste, nie dvakrát opísané. */
  const stavPreNahlad = useMemo(
    () => ({
      stlacene,
      typove,
      vsetkyUdaje,
      slovnik,
      selectedLanguage,
      pismenka,
      kombinacieSpolu,
      kombinaciePismena,
      zakaznicke,
      celyRiadok,
      ktoryNazov,
      nacrtky,
      schemy,
      najdene,
      najdenePopis,
      poznamka,
      najdenePoznamky,
      dlhsieRozsireneCislo,
      obchodak,
      zakladnaZlava,
      rozsirenaZlava,
    }),
    [
      stlacene,
      typove,
      vsetkyUdaje,
      slovnik,
      selectedLanguage,
      pismenka,
      kombinacieSpolu,
      kombinaciePismena,
      zakaznicke,
      celyRiadok,
      ktoryNazov,
      nacrtky,
      schemy,
      najdene,
      najdenePopis,
      poznamka,
      najdenePoznamky,
      dlhsieRozsireneCislo,
      obchodak,
      zakladnaZlava,
      rozsirenaZlava,
    ]
  );

  /* --- načítanie zo servera ----------------------------------------- */

  useEffect(() => {
    let zrusene = false;

    const nacitajStandard = async () => {
      const [standard, vseobPoznamky] = await Promise.all([
        axios.get(`${serverAdresa}standard`, { params: { typove } }),
        axios.get(`${serverAdresa}vseobPoznamky`),
      ]);

      // všeobecné poznámky doplnia tie, ktoré typové číslo nemá vlastné
      const data = {
        ...standard.data,
        poznamka: { ...vseobPoznamky.data[0], ...(standard.data.poznamka ?? {}) },
      };

      if (!zrusene) setVsetkyUdaje(data);
    };

    const nacitajSlovnik = async () => {
      const [vseob, poznamky] = await Promise.all([
        axios.get(`${serverAdresa}slovnikVseob`),
        axios.get(`${serverAdresa}slovnikPoznamky`),
      ]);
      if (!zrusene) setSlovnik({ ...vseob.data, ...poznamky.data });
    };

    const sOpakovanim = async (fn, pokusy = 3) => {
      for (let i = 0; i < pokusy; i++) {
        try {
          await fn();
          return;
        } catch (e) {
          if (i === pokusy - 1) console.error("Načítanie zlyhalo:", e);
        }
      }
    };

    sOpakovanim(nacitajStandard);
    sOpakovanim(nacitajSlovnik);

    return () => {
      zrusene = true;
    };
  }, [serverAdresa, typove]);

  /* --- obnova stavu z location.state -------------------------------- */

  useEffect(() => {
    const st = location.state;
    if (!st) return;
    const klice = Object.keys(st);

    // z landing page – dešifrovač
    if ("inputSearch" in st && "searchZapnuty" in st) {
      if ("stlacene" in st) setStlacene(st.stlacene);
      setInputSearch(st.inputSearch);
      setSearchZapnuty(st.searchZapnuty);
      return;
    }

    // z landing page – len jazyk (prípadne + obchodák)
    if (klice.length <= 2) {
      if ("selectedLanguage" in st) setSelectedLanguage(st.selectedLanguage);
      if ("obchodak" in st) setObchodak(st.obchodak);
      return;
    }

    // návrat z náhľadu – plná obnova konfigurácie
    setPismenka(st.pismenka);
    setStlacene(st.stlacene);
    setSelectedLanguage(st.selectedLanguage);
    setZakaznicke(st.zakaznicke);
    setCelyRiadok(st.celyRiadok);
    setKtoryNazov(st.ktoryNazov);
    setNacrtky(st.nacrtky);
    setSchemy(st.schemy);
    setNajdene(st.najdene);
    setNajdenePopis(st.najdenePopis);
    setNajdenePoznamky(st.najdenePoznamky);
    setObchodak(st.obchodak);
    setZakladnaZlava(st.zakladnaZlava);
    setRozsirenaZlava(st.rozsirenaZlava);
  }, [location.state]);

  /** Keď sa zmenia schémy, karusel začne od prvej. */
  useEffect(() => {
    setIndexSchema(0);
  }, [schemyText]);

  /** Ak si používateľ doplnil chýbajúce možnosti, pustíme ho na náhľad. */
  useEffect(() => {
    if (!nevybrateMoznosti || chybajuceTabulky) return;
    navigate("/nahlad", { state: stavPreNahlad });
  }, [nevybrateMoznosti, chybajuceTabulky, navigate, stavPreNahlad]);

  /* --- výnimky (zakázané kombinácie) -------------------------------- */

  const najdiVynimky = useCallback(
    (noveStlacene) => {
      const veci = prazdneNajdene();
      const popisy = prazdneNajdene();

      zakazanaMoznost.forEach((skupina) => {
        skupina.vynimky.forEach((vynimka) => {
          let count = 0;
          let letter = "";
          let index = 0;

          for (let j = 0; j < vynimka.length; j++) {
            const znakVynimky = String(vynimka[j]).toUpperCase();
            const znakStlaceny = String(noveStlacene[j] ?? "").toUpperCase();

            if (znakVynimky !== znakStlaceny && znakVynimky !== "X") {
              letter = vynimka[j];
              index = j;
              count++;
            }
            if (znakVynimky === "/") {
              letter = vynimka[j + 1];
              index = j;
              count++;
              break;
            }
            if (count > 1) break;
          }

          // presne jedna nezhoda = táto možnosť je zakázaná
          if (count === 1 && veci[index]) {
            veci[index].push(letter);
            popisy[index].push(skupina.popis);
          }
        });
      });

      setNajdene(veci);
      setNajdenePopis(popisy);
    },
    [zakazanaMoznost]
  );

  /* --- klik v tabuľke ----------------------------------------------- */

  const handleClick = useCallback(
    (riadok, indexTabulky, kolkyNazov) => {
      // posledný stĺpec môže byť cena – potom je označenie predposledné
      const posledny = riadok[riadok.length - 1];
      const elem =
        typeof posledny === "number" ? riadok[riadok.length - 2] : posledny;

      // zakázanú možnosť ignorujeme
      if (najdene[indexTabulky]?.includes(elem)) return;

      const jeOdtuknutie =
        JSON.stringify(riadok) === JSON.stringify(celyRiadok[indexTabulky]);
      const zakaznickeVyhotovenie = elem === "X";
      // tabuľka1 sedí na pozícii 0, ostatné majú číslo = pozícia
      const cisloTabulky = indexTabulky === 0 ? 1 : indexTabulky;

      /* --- názov, schémy, náčrty --- */
      if (jeOdtuknutie) {
        setKtoryNazov((p) => nahradNaIndexe(p, indexTabulky, ""));
        setSchemy((p) => nahradNaIndexe(p, indexTabulky, ""));
        setNacrtky((p) => nahradNaIndexe(p, indexTabulky, ""));
      } else {
        const nazvy = vsetkyUdaje[`tabulka${cisloTabulky}`][kolkyNazov].nazvy;
        setKtoryNazov((p) => nahradNaIndexe(p, indexTabulky, nazvy));

        if (!zakaznickeVyhotovenie) {
          const { schema, nacrt } = vytiahniSchemuANacrt(riadok, nazvy);
          // null = tabuľka taký stĺpec nemá, predchádzajúcu hodnotu nechávame
          if (schema !== null) {
            setSchemy((p) => nahradNaIndexe(p, indexTabulky, schema));
          }
          if (nacrt !== null) {
            setNacrtky((p) => nahradNaIndexe(p, indexTabulky, nacrt));
          }
        }
      }

      /* --- celý riadok a poznámky --- */
      if (jeOdtuknutie) {
        setCelyRiadok((p) => nahradNaIndexe(p, indexTabulky, []));
        setNajdenePoznamky((p) => nahradNaIndexe(p, indexTabulky, []));
        setKonektor(false);
      } else {
        setCelyRiadok((p) => nahradNaIndexe(p, indexTabulky, riadok));

        if (!zakaznickeVyhotovenie) {
          const poznamkyRiadku = poznamkyZRiadka(riadok);
          if (poznamkyRiadku) {
            setNajdenePoznamky((p) =>
              nahradNaIndexe(p, indexTabulky, poznamkyRiadku)
            );
          }
        }
      }

      /* --- objednávací kód --- */
      const znovuKliknute =
        !zakaznickeVyhotovenie && stlacene[indexTabulky] === elem;
      const noveStlacene = nahradNaIndexe(
        stlacene,
        indexTabulky,
        znovuKliknute ? "x" : elem
      );

      setStlacene(noveStlacene);
      najdiVynimky(noveStlacene);

      if (!znovuKliknute) {
        setKonektor(String(riadok[0]).split(" ").includes("konektor"));
      }
    },
    [najdene, celyRiadok, stlacene, vsetkyUdaje, najdiVynimky]
  );

  /* --- dešifrovač ---------------------------------------------------- */

  const handleSearchDesifrovac = (e) => {
    e.preventDefault();

    if (!inputSearch.includes("/")) {
      setError(t("chybaLomitko"));
      return;
    }
    setError(null);

    const { typoveHladane, pozicie } = rozparsujObjednavaciKod(inputSearch);

    if (typoveHladane !== typove) {
      navigate(`/serva/${typoveHladane}`, {
        state: { inputSearch, searchZapnuty, stlacene: pozicie },
      });
      window.location.reload();
      return;
    }

    setStlacene(pozicie);
  };

  /* --- PDF ----------------------------------------------------------- */

  const stiahniPdf = async (typPdf) => {
    setNacitava(true);
    try {
      const response = await axios.get(`${serverAdresa}${typPdf}`, {
        responseType: "blob",
        params: {
          typove,
          schemyVsetky: schemyList,
          celyRiadok: JSON.stringify(celyRiadok),
        },
      });

      const url = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const predpona =
        typPdf === "generateSchemy" ? "schemy_regada" : "nacrty_regada";

      const link = document.createElement("a");
      link.href = url;
      link.download = `${predpona}_${typove}_${vsetkyUdaje.nazov}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Chyba pri generovaní PDF:", err);
    } finally {
      setNacitava(false);
    }
  };

  /* --- karusel schém -------------------------------------------------- */

  const predchadzajucaSchema = () =>
    setIndexSchema((i) =>
      schemyList.length ? (i - 1 + schemyList.length) % schemyList.length : 0
    );

  const dalsiaSchema = () =>
    setIndexSchema((i) =>
      schemyList.length ? (i + 1) % schemyList.length : 0
    );

  /* --- render -------------------------------------------------------- */

  if (!vsetkyUdaje || !slovnik || nacitava) return <Loading />;

  return (
    <>
      <Nav
        onSelectLanguage={setSelectedLanguage}
        defaultLang={selectedLanguage}
        presmerovanie={"https://www.regada.sk/elektricke-servopohony"}
      />

      <div className="obalena_tab1">
        <div className="nazov_obrazok">
          <div className="detail">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <h1 id="nazov">{vsetkyUdaje.nazov}</h1>

                {!searchZapnuty ? (
                  <FaSearch
                    id="search-icon"
                    style={{
                      height: "20px",
                      width: "20px",
                      marginLeft: "12px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSearchZapnuty(true)}
                  />
                ) : (
                  <>
                    <div className="input-wrapper" style={{ marginLeft: "30px" }}>
                      <FaSearch id="search-icon" />
                      <input
                        className="cislo_objednavacie"
                        placeholder="Objednávaci kód..."
                        onChange={(e) => setInputSearch(e.target.value)}
                        value={inputSearch}
                        maxLength={25}
                      />
                      <img
                        onClick={handleSearchDesifrovac}
                        src={sipka_find}
                        alt="sipka"
                        className="checkmark"
                      />
                    </div>
                    <ClearIcon
                      style={{ cursor: "pointer", marginLeft: "10px" }}
                      onClick={() => {
                        setSearchZapnuty(false);
                        setError(null);
                      }}
                    />
                  </>
                )}
              </div>
            </div>

            {error && (
              <div style={{ color: "#c0392b", marginBottom: "12px" }}>
                {error}
              </div>
            )}

            {obchodak && <h2>Režim: Obchodné oddelenie</h2>}

            <div id="vybavenia_obvidva">
              <div hidden={nevybrateMoznosti} className="vybavenia_jedno">
                <h2 className="vybavenia_nazvy">{t("standardneVybavenie")}</h2>
                <ul>
                  <Vybavenie
                    vybaveniaData={vsetkyUdaje.vybavenie.standardne}
                    slovnik={slovnik}
                    selectedLanguage={selectedLanguage}
                    poznamka={poznamka}
                  />
                </ul>
              </div>

              <div hidden={nevybrateMoznosti}>
                <h2 className="vybavenia_nazvy">{t("rozsireneVybavenie")}</h2>
                <ul>
                  <Vybavenie
                    vybaveniaData={vsetkyUdaje.vybavenie.rozsirene}
                    slovnik={slovnik}
                    selectedLanguage={selectedLanguage}
                    poznamka={poznamka}
                  />
                </ul>
              </div>
            </div>

            <div className="nezvolene_tabulky" hidden={!nevybrateMoznosti}>
              <div hidden={!stlacene.includes("x")}>{t("nezvolene")}</div>

              {INDEXY_TABULIEK.filter((index) => stlacene[index] === "x").map(
                (index) => {
                  const cislo = index === 0 ? 1 : index;
                  const nazov = nazovTabulky(vsetkyUdaje[`tabulka${cislo}`]);
                  return (
                    <div key={index}>
                      {cislo}. {prelozSlovnik(slovnik, nazov, selectedLanguage)}
                    </div>
                  );
                }
              )}
            </div>
          </div>

          <img
            className="hlavny_obrazok pravy_margin-konfi"
            src={`${BASE_URL_OBRAZKY}/servopohony/${vsetkyUdaje.kod}.png`}
            alt={vsetkyUdaje.nazov}
          />
        </div>

        <div className="board lavy_margin-konfi">
          <div id="objednavaci_kod">
            <div className="objednavaci_riadok" id="order_code_label">
              {t("objednavaciKod")}
            </div>
            <div className="prava_strana">
              <div id="kodmaly" className="objednavaci_riadok">
                {typove.length > 3
                  ? `${typove.slice(0, 2)} ${typove.slice(2)}`
                  : typove}
                .
              </div>
              <div className="kod_cisla">
                {stlacene.map((znak, i) => (
                  <div className="objednavaci_riadok cislo" key={`${i}-${znak}`}>
                    {znak}
                  </div>
                ))}
              </div>
              <RozsireneCisla
                letters={pismenka}
                kod={kodRozsireneho}
                pocetCislic={pocetCislic}
              />
            </div>
          </div>

          <div className="mobile_objednavaci">
            <div className="prava_strana">
              {typove}.{stlacene.join("")}
              <RozsireneCisla
                letters={pismenka}
                kod={kodRozsireneho}
                pocetCislic={pocetCislic}
              />
            </div>
          </div>

          <div className="container">
            {CLASSY_TABULKY.map((classa, i) => (
              <ConfigTabulka
                key={classa}
                tabulka={vsetkyUdaje[`tabulka${i + 1}`]}
                cisloHlavnejTab={i === 0 ? 0 : i + 1}
                idPreTabulku={classa}
                classPreTabulka={
                  dlhsieRozsireneCislo === 3
                    ? `${classa}_tabulka-3`
                    : `${classa}_tabulka`
                }
                stlacene={stlacene}
                slovnik={slovnik}
                selectedLanguage={selectedLanguage}
                nevybrateMoznosti={nevybrateMoznosti}
                najdene={najdene}
                najdenePopis={najdenePopis}
                poznamka={poznamka}
                updateKtoraTab={setKtoraTab}
                celyRiadok={celyRiadok}
                ktoryNazov={ktoryNazov}
                nacrtky={nacrtky}
                schemy={schemy}
                vsetkyUdaje={vsetkyUdaje}
                konektor={konektor}
                updateSchemy={setSchemy}
                updateNacrtky={setNacrtky}
                updateKtoryNazov={setKtoryNazov}
                updateNajdenePoznamky={setNajdenePoznamky}
                updateKonektor={setKonektor}
                updateCelyRiadok={setCelyRiadok}
                updateStlacene={setStlacene}
                najdenePoznamky={najdenePoznamky}
                zakazana_moznost={zakazanaMoznost}
                updateNajdenePopis={setNajdenePopis}
                updateNajdene={setNajdene}
                zakaznicke={zakaznicke}
                updateZakaznicke={setZakaznicke}
                onHandleClick={handleClick}
              />
            ))}

            {vsetkyUdaje.tabulka7 && (
              <SiedmaTabulkaConfi
                nevybrateMoznosti={nevybrateMoznosti}
                vsetkyUdaje={vsetkyUdaje}
                slovnik={slovnik}
                selectedLanguage={selectedLanguage}
                pismenka={pismenka}
                kombinaciePismena={kombinaciePismena}
                najdene={najdene}
                kombinacieSpolu={kombinacieSpolu}
                updateKtoraTab={setKtoraTab}
                nastavitPismenka={setPismenka}
                konektor={konektor}
                poznamka={poznamka}
                schemy={schemy}
                updateSchemy={setSchemy}
              />
            )}

            <div
              className="tlacitko"
              hidden={!nevybrateMoznosti}
              onClick={() => setNevybrateMoznosti(false)}
            >
              {t("zobrazitVsetko")}
            </div>
          </div>
        </div>

        <div className="fixed-bottom-right hidePhones">
          {/* NÁČRTY */}
          <div className={maNacrty ? "oknodole" : "oknodole sedive"}>
            <div className="obsah_okno_dole">
              <div className="nacrty_schemy">
                <div className="kod_dole">{t("zvoleneNacrty")}</div>
                {maNacrty ? (
                  <div className="nacrt">
                    {nacrtky.join("")}/{stlacene[6] ?? null}
                  </div>
                ) : (
                  <div className="cisla_dole">{t("ziadne")}</div>
                )}
              </div>

              <div
                className={maNacrty ? "tlacitko-male" : "sedive-tlacitko-male "}
                onClick={() => maNacrty && setOpenNacrtImg(true)}
              >
                {t("ukazat")}
              </div>

              <div onClick={() => maNacrty && stiahniPdf("generateNacrtky")}>
                <img
                  src={ulozitImage}
                  style={{
                    width: "20px",
                    cursor: maNacrty ? "pointer" : "default",
                  }}
                  alt="download"
                />
              </div>
            </div>
          </div>

          <Dialog
            open={openNacrtImg}
            onClose={() => setOpenNacrtImg(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Rozmerový náčrt
              <IconButton
                aria-label="close"
                onClick={() => setOpenNacrtImg(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ textAlign: "center" }}>
              <img
                src={`${BASE_URL_OBRAZKY}/nacrty/${nacrtky[6] ?? ""}-${
                  stlacene[6]
                }.png`}
                alt="preview"
                style={{ maxWidth: "100%", borderRadius: "8px" }}
              />
            </DialogContent>
          </Dialog>

          {/* SCHÉMY */}
          <div className={maSchemy ? "oknodole" : "oknodole sedive"}>
            <div className="obsah_okno_dole">
              <div className="nacrty_schemy">
                <div className="kod_dole">{t("zvoleneSchemy")}</div>
                {maSchemy ? (
                  <div className="nacrt">{schemyText}</div>
                ) : (
                  <div className="cisla_dole">{t("ziadne")}</div>
                )}
              </div>

              <div
                className={maSchemy ? "tlacitko-male" : "sedive-tlacitko-male "}
                onClick={() => maSchemy && setOpenSchemyImg(true)}
              >
                {t("ukazat")}
              </div>

              <div onClick={() => maSchemy && stiahniPdf("generateSchemy")}>
                <img
                  src={ulozitImage}
                  style={{
                    width: "20px",
                    cursor: maSchemy ? "pointer" : "default",
                  }}
                  alt="download"
                />
              </div>
            </div>
          </div>

          <Dialog
            open={openSchemyImg}
            onClose={() => setOpenSchemyImg(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Schémy
              <IconButton
                aria-label="close"
                onClick={() => setOpenSchemyImg(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ textAlign: "center", position: "relative" }}>
              <IconButton
                onClick={predchadzajucaSchema}
                sx={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              <img
                src={obrazkySchem[indexSchema]}
                alt={`preview-${indexSchema}`}
                style={{ maxWidth: "100%", borderRadius: "8px" }}
              />

              <IconButton
                onClick={dalsiaSchema}
                sx={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </DialogContent>
          </Dialog>

          {/* POKRAČOVAŤ / ZAKÁZANÁ KOMBINÁCIA */}
          {kombinaciaPovolena ? (
            <div
              className="tlacitko-konfi"
              onClick={() => {
                if (chybajuceTabulky) {
                  setNevybrateMoznosti(true);
                  window.scrollTo(0, 0);
                  return;
                }
                navigate("/nahlad", { state: stavPreNahlad });
              }}
            >
              {t("pokracovat")}
            </div>
          ) : (
            <div className="tlacitko-chyba-konfi">{t("zakazana")}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default Configurator;